import {
  type RealtimeMediaSessionMetadata,
  type RealtimeMediaSignalPayload,
  type RealtimeSessionControlStateContract,
  type RealtimeVoiceFallbackContract,
  type RealtimeVoiceProviderSessionEvidenceContract,
  type RealtimeVoiceSessionStartResponse,
} from '@automatick/shared-ng';
import {
  AutomatickEvent,
  RealtimeMediaSignalKind,
  type RealtimeMediaSignalKind as RealtimeMediaSignalKindValue,
} from '@automatick/shared-ts';

export type CanonicalRealtimeReplyLevel = 'user' | 'node' | 'session';

export interface CanonicalRealtimeReplyRecipient {
  readonly level: CanonicalRealtimeReplyLevel;
  readonly key: string;
}

export interface CanonicalRealtimeControlState {
  readonly action?: string | null;
  readonly controlSessionId?: string;
  readonly currentControllerKind?: string | null;
  readonly currentControllerId?: string | null;
  readonly requestedControllerKind?: string | null;
  readonly requestedControllerId?: string | null;
  readonly state: string | null;
  readonly stateVersion: number | null;
}

export interface CanonicalRealtimeVoiceProviderEvidence {
  readonly isProviderBacked?: boolean;
  readonly providerSessionId?: string | null;
}

export interface CanonicalRealtimeVoiceFallback {
  readonly fallbackKind?: string | null;
  readonly isRealtimeStillActive?: boolean;
  readonly reasonCode?: string | null;
}

export interface CanonicalRealtimeMediaMetadata {
  readonly title?: string | null;
  readonly sessionType?: string | null;
  readonly sessionIntent?: string | null;
  readonly sessionId?: string | null;
  readonly sessionEpoch?: number;
  readonly state?: string | null;
  readonly controlState?: CanonicalRealtimeControlState;
  readonly requestedMediaKinds?: readonly string[] | null;
  readonly preferredSourceKinds?: readonly string[] | null;
  readonly realtimeVoiceProvider?: CanonicalRealtimeVoiceProviderEvidence;
  readonly realtimeVoiceFallback?: CanonicalRealtimeVoiceFallback;
}

export interface CanonicalRealtimeMediaSignal {
  readonly mediaSessionId: string;
  readonly signalKind: RealtimeMediaSignalKindValue;
  readonly payload: RealtimeMediaSignalPayload | null;
  readonly metadata: CanonicalRealtimeMediaMetadata | null;
  readonly providerSession: CanonicalRealtimeVoiceProviderEvidence | null;
  readonly fallback: CanonicalRealtimeVoiceFallback | null;
  readonly requestedMediaKinds: readonly string[];
  readonly preferredSourceKinds: readonly string[];
  readonly title: string | null;
  readonly replyRecipients: readonly CanonicalRealtimeReplyRecipient[];
}

export interface CanonicalRealtimeVoiceStartEvidence {
  readonly providerSession: RealtimeVoiceProviderSessionEvidenceContract | null;
  readonly fallback: RealtimeVoiceFallbackContract | null;
}

interface NotificationCandidate {
  readonly eventName?: unknown;
  readonly realtimeCommunication?: unknown;
}

interface CommunicationCandidate {
  readonly actor?: unknown;
  readonly payload?: unknown;
}

interface SignalCandidate {
  readonly mediaSessionId?: unknown;
  readonly signalKind?: unknown;
  readonly payload?: unknown;
  readonly metadata?: unknown;
  readonly context?: unknown;
}

interface SignalPayloadCandidate {
  readonly type?: unknown;
  readonly sdp?: unknown;
  readonly candidate?: unknown;
  readonly sdpMid?: unknown;
  readonly sdpMLineIndex?: unknown;
  readonly usernameFragment?: unknown;
  readonly restartIce?: unknown;
}

interface MetadataCandidate {
  readonly title?: unknown;
  readonly sessionType?: unknown;
  readonly sessionIntent?: unknown;
  readonly sessionId?: unknown;
  readonly sessionEpoch?: unknown;
  readonly state?: unknown;
  readonly controlState?: unknown;
  readonly requestedMediaKinds?: unknown;
  readonly preferredSourceKinds?: unknown;
  readonly realtimeVoiceProvider?: unknown;
  readonly realtimeVoiceFallback?: unknown;
  readonly providerSessionEvidence?: unknown;
  readonly fallback?: unknown;
}

interface ControlStateCandidate {
  readonly action?: unknown;
  readonly controlSessionId?: unknown;
  readonly currentControllerKind?: unknown;
  readonly currentControllerId?: unknown;
  readonly requestedControllerKind?: unknown;
  readonly requestedControllerId?: unknown;
  readonly state?: unknown;
  readonly stateVersion?: unknown;
}

interface ActorCandidate {
  readonly userId?: unknown;
  readonly nodeId?: unknown;
  readonly sessionId?: unknown;
}

interface ProviderEvidenceCandidate {
  readonly isProviderBacked?: unknown;
  readonly providerSessionId?: unknown;
}

interface FallbackCandidate {
  readonly fallbackKind?: unknown;
  readonly isRealtimeStillActive?: unknown;
  readonly reasonCode?: unknown;
}

const invalidContract = Symbol('invalid realtime media contract');

export function decodeRealtimeMediaSignalNotification(
  value: unknown,
): CanonicalRealtimeMediaSignal | null {
  if (
    !isNotificationCandidate(value) ||
    value.eventName !== AutomatickEvent.RealtimeMediaSignal ||
    !isCommunicationCandidate(value.realtimeCommunication)
  ) {
    return null;
  }

  const communication = value.realtimeCommunication;
  const signal = communication.payload;
  if (
    !isSignalCandidate(signal) ||
    signal.context !== undefined ||
    !isCanonicalRequiredString(signal.mediaSessionId)
  ) {
    return null;
  }

  const signalKind = decodeSignalKind(signal.signalKind);
  const payload = decodeSignalPayload(signal.payload);
  const metadata = decodeMetadata(signal.metadata);
  const replyRecipients = decodeReplyRecipients(communication.actor);
  if (
    signalKind === null ||
    payload === invalidContract ||
    metadata === invalidContract ||
    replyRecipients === invalidContract
  ) {
    return null;
  }

  return Object.freeze({
    mediaSessionId: signal.mediaSessionId,
    signalKind,
    payload,
    metadata,
    providerSession: metadata?.realtimeVoiceProvider ?? null,
    fallback: metadata?.realtimeVoiceFallback ?? null,
    requestedMediaKinds: metadata?.requestedMediaKinds ?? Object.freeze([]),
    preferredSourceKinds: metadata?.preferredSourceKinds ?? Object.freeze([]),
    title: metadata?.title ?? null,
    replyRecipients,
  });
}

export function projectRealtimeMediaSessionMetadata(
  metadata: CanonicalRealtimeMediaMetadata | null,
): RealtimeMediaSessionMetadata | null {
  if (!metadata) {
    return null;
  }

  const projection: RealtimeMediaSessionMetadata = {};
  if (metadata.title !== undefined) {
    projection.title = metadata.title;
  }
  if (metadata.sessionType !== undefined) {
    projection.sessionType = metadata.sessionType;
  }
  if (metadata.sessionIntent !== undefined) {
    projection.sessionIntent = metadata.sessionIntent;
  }
  if (metadata.sessionId !== undefined) {
    projection.sessionId = metadata.sessionId;
  }
  if (metadata.sessionEpoch !== undefined) {
    projection.sessionEpoch = metadata.sessionEpoch;
  }
  if (metadata.state !== undefined) {
    projection.state = metadata.state;
  }
  if (metadata.controlState !== undefined) {
    const controlState: RealtimeSessionControlStateContract = {
      state: metadata.controlState.state,
      stateVersion: metadata.controlState.stateVersion ?? undefined,
    };
    if (metadata.controlState.action !== undefined) {
      controlState.action = metadata.controlState.action;
    }
    if (metadata.controlState.controlSessionId !== undefined) {
      controlState.controlSessionId = metadata.controlState.controlSessionId;
    }
    if (metadata.controlState.currentControllerKind !== undefined) {
      controlState.currentControllerKind =
        metadata.controlState.currentControllerKind;
    }
    if (metadata.controlState.currentControllerId !== undefined) {
      controlState.currentControllerId =
        metadata.controlState.currentControllerId;
    }
    if (metadata.controlState.requestedControllerKind !== undefined) {
      controlState.requestedControllerKind =
        metadata.controlState.requestedControllerKind;
    }
    if (metadata.controlState.requestedControllerId !== undefined) {
      controlState.requestedControllerId =
        metadata.controlState.requestedControllerId;
    }
    projection.controlState = controlState;
  }
  if (metadata.requestedMediaKinds !== undefined) {
    projection.requestedMediaKinds = metadata.requestedMediaKinds
      ? [...metadata.requestedMediaKinds]
      : null;
  }
  if (metadata.preferredSourceKinds !== undefined) {
    projection.preferredSourceKinds = metadata.preferredSourceKinds
      ? [...metadata.preferredSourceKinds]
      : null;
  }
  if (metadata.realtimeVoiceProvider !== undefined) {
    projection.realtimeVoiceProvider = metadata.realtimeVoiceProvider;
  }
  if (metadata.realtimeVoiceFallback !== undefined) {
    projection.realtimeVoiceFallback = metadata.realtimeVoiceFallback;
  }
  return projection;
}

export function projectRealtimeVoiceStartEvidence(
  response: RealtimeVoiceSessionStartResponse,
): CanonicalRealtimeVoiceStartEvidence {
  return Object.freeze({
    providerSession: cloneProviderEvidence(response.providerSessionEvidence),
    fallback: response.fallback ? { ...response.fallback } : null,
  });
}

export function projectRealtimeIceCandidatePayload(
  candidate: RTCIceCandidate,
): RealtimeMediaSignalPayload {
  const value = candidate.toJSON();
  return {
    candidate: value.candidate,
    sdpMid: value.sdpMid,
    sdpMLineIndex: value.sdpMLineIndex,
    usernameFragment: value.usernameFragment,
  };
}

export function projectRtcIceCandidate(
  payload: RealtimeMediaSignalPayload | null,
): RTCIceCandidateInit | null {
  if (!isCanonicalRequiredString(payload?.candidate)) {
    return null;
  }

  return {
    candidate: payload.candidate,
    sdpMid: payload.sdpMid ?? null,
    sdpMLineIndex: payload.sdpMLineIndex ?? null,
    usernameFragment: payload.usernameFragment ?? null,
  };
}

function decodeSignalKind(value: unknown): RealtimeMediaSignalKindValue | null {
  switch (value) {
    case RealtimeMediaSignalKind.Invite:
    case RealtimeMediaSignalKind.Offer:
    case RealtimeMediaSignalKind.Answer:
    case RealtimeMediaSignalKind.IceCandidate:
    case RealtimeMediaSignalKind.Renegotiate:
    case RealtimeMediaSignalKind.State:
    case RealtimeMediaSignalKind.Ended:
    case RealtimeMediaSignalKind.Ringing:
    case RealtimeMediaSignalKind.Busy:
    case RealtimeMediaSignalKind.Declined:
    case RealtimeMediaSignalKind.Missed:
    case RealtimeMediaSignalKind.TimedOut:
    case RealtimeMediaSignalKind.AcceptedElsewhere:
      return value;
    default:
      return null;
  }
}

function decodeSignalPayload(
  value: unknown,
): RealtimeMediaSignalPayload | null | typeof invalidContract {
  if (value === undefined) {
    return null;
  }
  if (
    !isSignalPayloadCandidate(value) ||
    !isOptionalNullableString(value.type) ||
    !isOptionalNullableString(value.sdp) ||
    !isOptionalNullableString(value.candidate) ||
    !isOptionalNullableString(value.sdpMid) ||
    !isOptionalNullableInteger(value.sdpMLineIndex) ||
    !isOptionalNullableString(value.usernameFragment) ||
    !isOptionalBoolean(value.restartIce)
  ) {
    return invalidContract;
  }

  return Object.freeze({
    type: value.type,
    sdp: value.sdp,
    candidate: value.candidate,
    sdpMid: value.sdpMid,
    sdpMLineIndex: value.sdpMLineIndex,
    usernameFragment: value.usernameFragment,
    restartIce: value.restartIce,
  });
}

function decodeMetadata(
  value: unknown,
): CanonicalRealtimeMediaMetadata | null | typeof invalidContract {
  if (value === undefined) {
    return null;
  }
  if (!isMetadataCandidate(value)) {
    return invalidContract;
  }

  try {
    const title = value.title;
    const sessionType = value.sessionType;
    const sessionIntent = value.sessionIntent;
    const sessionId = value.sessionId;
    const sessionEpoch = value.sessionEpoch;
    const state = value.state;
    const controlStateSource = value.controlState;
    const requestedMediaKinds = value.requestedMediaKinds;
    const preferredSourceKinds = value.preferredSourceKinds;
    const realtimeVoiceProvider = value.realtimeVoiceProvider;
    const realtimeVoiceFallback = value.realtimeVoiceFallback;
    const providerSessionEvidence = value.providerSessionEvidence;
    const legacyFallback = value.fallback;
    if (
      providerSessionEvidence !== undefined ||
      legacyFallback !== undefined ||
      !isOptionalNullableString(title) ||
      !isOptionalNullableCanonicalString(sessionType) ||
      !isOptionalNullableCanonicalString(sessionIntent) ||
      !isOptionalNullableCanonicalString(sessionId) ||
      !isOptionalPositiveSafeInteger(sessionEpoch) ||
      !isOptionalNullableCanonicalString(state) ||
      !isOptionalCanonicalStringArray(requestedMediaKinds) ||
      !isOptionalCanonicalStringArray(preferredSourceKinds)
    ) {
      return invalidContract;
    }

    const controlState = decodeControlState(controlStateSource);
    const providerSession = decodeProviderEvidence(realtimeVoiceProvider);
    const fallback = decodeFallback(realtimeVoiceFallback);
    if (
      controlState === invalidContract ||
      providerSession === invalidContract ||
      fallback === invalidContract
    ) {
      return invalidContract;
    }

    return Object.freeze({
      title: title === undefined ? undefined : normalizeText(title),
      sessionType,
      sessionIntent,
      sessionId,
      ...(sessionEpoch === undefined ? {} : { sessionEpoch }),
      state,
      controlState: controlState ?? undefined,
      requestedMediaKinds:
        requestedMediaKinds == null
          ? requestedMediaKinds
          : Object.freeze([...requestedMediaKinds]),
      preferredSourceKinds:
        preferredSourceKinds == null
          ? preferredSourceKinds
          : Object.freeze([...preferredSourceKinds]),
      realtimeVoiceProvider: providerSession ?? undefined,
      realtimeVoiceFallback: fallback ?? undefined,
    });
  } catch {
    return invalidContract;
  }
}

function decodeControlState(
  value: unknown,
): CanonicalRealtimeControlState | null | typeof invalidContract {
  if (value === undefined) {
    return null;
  }
  if (!isControlStateCandidate(value)) {
    return invalidContract;
  }

  try {
    const action = value.action;
    const controlSessionId = value.controlSessionId;
    const currentControllerKind = value.currentControllerKind;
    const currentControllerId = value.currentControllerId;
    const requestedControllerKind = value.requestedControllerKind;
    const requestedControllerId = value.requestedControllerId;
    const state = value.state;
    const stateVersion = value.stateVersion;
    if (
      !isOptionalNullableCanonicalString(action) ||
      !isOptionalCanonicalString(controlSessionId) ||
      !isOptionalNullableCanonicalString(currentControllerKind) ||
      !isOptionalNullableCanonicalString(currentControllerId) ||
      !isOptionalNullableCanonicalString(requestedControllerKind) ||
      !isOptionalNullableCanonicalString(requestedControllerId) ||
      !isOptionalNullableCanonicalString(state) ||
      !isOptionalNonnegativeInteger(stateVersion)
    ) {
      return invalidContract;
    }

    return Object.freeze({
      ...(action === undefined ? {} : { action }),
      ...(controlSessionId === undefined ? {} : { controlSessionId }),
      ...(currentControllerKind === undefined ? {} : { currentControllerKind }),
      ...(currentControllerId === undefined ? {} : { currentControllerId }),
      ...(requestedControllerKind === undefined
        ? {}
        : { requestedControllerKind }),
      ...(requestedControllerId === undefined ? {} : { requestedControllerId }),
      state: state ?? null,
      stateVersion: stateVersion ?? null,
    });
  } catch {
    return invalidContract;
  }
}

function decodeProviderEvidence(
  value: unknown,
): CanonicalRealtimeVoiceProviderEvidence | null | typeof invalidContract {
  if (value === undefined) {
    return null;
  }
  if (
    !isProviderEvidenceCandidate(value) ||
    !isOptionalBoolean(value.isProviderBacked) ||
    !isOptionalNullableCanonicalString(value.providerSessionId)
  ) {
    return invalidContract;
  }

  return Object.freeze({
    isProviderBacked: value.isProviderBacked,
    providerSessionId: value.providerSessionId,
  });
}

function decodeFallback(
  value: unknown,
): CanonicalRealtimeVoiceFallback | null | typeof invalidContract {
  if (value === undefined) {
    return null;
  }
  if (
    !isFallbackCandidate(value) ||
    !isOptionalNullableCanonicalString(value.fallbackKind) ||
    !isOptionalBoolean(value.isRealtimeStillActive) ||
    !isOptionalNullableCanonicalString(value.reasonCode)
  ) {
    return invalidContract;
  }

  return Object.freeze({
    fallbackKind: value.fallbackKind,
    isRealtimeStillActive: value.isRealtimeStillActive,
    reasonCode: value.reasonCode,
  });
}

function decodeReplyRecipients(
  value: unknown,
): readonly CanonicalRealtimeReplyRecipient[] | typeof invalidContract {
  if (value === undefined || value === null) {
    return Object.freeze([]);
  }
  if (
    !isActorCandidate(value) ||
    !isOptionalNullableCanonicalString(value.userId) ||
    !isOptionalNullableCanonicalString(value.nodeId) ||
    !isOptionalNullableCanonicalString(value.sessionId)
  ) {
    return invalidContract;
  }

  const recipients: CanonicalRealtimeReplyRecipient[] = [];
  addReplyRecipient(recipients, 'user', value.userId);
  addReplyRecipient(recipients, 'node', value.nodeId);
  addReplyRecipient(recipients, 'session', value.sessionId);
  return Object.freeze(recipients);
}

function addReplyRecipient(
  recipients: CanonicalRealtimeReplyRecipient[],
  level: CanonicalRealtimeReplyLevel,
  value: string | null | undefined,
): void {
  if (value) {
    recipients.push(Object.freeze({ level, key: value }));
  }
}

function cloneOptionalArray(
  values: Array<string> | null | undefined,
): Array<string> | null | undefined {
  return values == null ? values : [...values];
}

function cloneProviderEvidence(
  value: RealtimeVoiceProviderSessionEvidenceContract | undefined,
): RealtimeVoiceProviderSessionEvidenceContract | null {
  if (!value) {
    return null;
  }

  const projection: RealtimeVoiceProviderSessionEvidenceContract = {
    ...value,
  };
  if (value.endpointSelection) {
    projection.endpointSelection = {
      ...value.endpointSelection,
      requestedMediaKinds: cloneOptionalArray(
        value.endpointSelection.requestedMediaKinds,
      ),
    };
  }
  if (value.mediaAttachment) {
    projection.mediaAttachment = {
      ...value.mediaAttachment,
      requestedMediaKinds: cloneOptionalArray(
        value.mediaAttachment.requestedMediaKinds,
      ),
      modalities: cloneOptionalArray(value.mediaAttachment.modalities),
    };
  }
  return projection;
}

function normalizeText(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

function isCanonicalRequiredString(value: unknown): value is string {
  return (
    typeof value === 'string' && value.length > 0 && value === value.trim()
  );
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalNullableString(
  value: unknown,
): value is string | null | undefined {
  return value === null || isOptionalString(value);
}

function isOptionalNullableCanonicalString(
  value: unknown,
): value is string | null | undefined {
  return (
    value === undefined || value === null || isCanonicalRequiredString(value)
  );
}

function isOptionalCanonicalString(
  value: unknown,
): value is string | undefined {
  return value === undefined || isCanonicalRequiredString(value);
}

function isOptionalPositiveSafeInteger(
  value: unknown,
): value is number | undefined {
  return (
    value === undefined ||
    (typeof value === 'number' && Number.isSafeInteger(value) && value > 0)
  );
}

function isOptionalBoolean(value: unknown): value is boolean | undefined {
  return value === undefined || typeof value === 'boolean';
}

function isOptionalNonnegativeInteger(
  value: unknown,
): value is number | undefined {
  return (
    value === undefined ||
    (typeof value === 'number' && Number.isInteger(value) && value >= 0)
  );
}

function isOptionalNullableInteger(
  value: unknown,
): value is number | null | undefined {
  return value === null || isOptionalNonnegativeInteger(value);
}

function isOptionalCanonicalStringArray(
  value: unknown,
): value is readonly string[] | null | undefined {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.every(isCanonicalRequiredString))
  );
}

function isPlainObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNotificationCandidate(
  value: unknown,
): value is NotificationCandidate {
  return isPlainObject(value);
}

function isCommunicationCandidate(
  value: unknown,
): value is CommunicationCandidate {
  return isPlainObject(value);
}

function isSignalCandidate(value: unknown): value is SignalCandidate {
  return isPlainObject(value);
}

function isSignalPayloadCandidate(
  value: unknown,
): value is SignalPayloadCandidate {
  return isPlainObject(value);
}

function isMetadataCandidate(value: unknown): value is MetadataCandidate {
  return isPlainObject(value);
}

function isControlStateCandidate(
  value: unknown,
): value is ControlStateCandidate {
  return isPlainObject(value);
}

function isActorCandidate(value: unknown): value is ActorCandidate {
  return isPlainObject(value);
}

function isProviderEvidenceCandidate(
  value: unknown,
): value is ProviderEvidenceCandidate {
  return isPlainObject(value);
}

function isFallbackCandidate(value: unknown): value is FallbackCandidate {
  return isPlainObject(value);
}
